// src/pages/Hub/components/ReportsDeck.js
//
// Reports & data deck: FSR completions, finance (petty cash + purchases),
// attendance (range-aware), and tracker telemetry — fed by /api/ops-hub/reports.
// Compliance & EHS are scored against the C&I roster (commercial_teams members) so
// the tiles read "done / roster"; the drill-down lists exactly who did and who didn't.
//
// Every tile is clickable: it opens the detail drawer (kind 'report') with a
// breakdown of the figures, plus a real-record list for the high-value tiles.

import React from 'react';
import { motion } from 'framer-motion';
import {
  ClipboardCheck, Wallet, UserCheck, Radio, ShieldCheck, CheckSquare, ChevronRight,
} from 'lucide-react';
import { C, regionMeta, fmtMoney } from '../theme';

const card = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 220, damping: 22 } },
};

const sumBy = (rows, key) => (rows || []).reduce((a, r) => a + (Number(r[key]) || 0), 0);
const findStatus = (rows, names) =>
  (rows || []).filter((r) => names.includes(String(r.status || '').toLowerCase())).reduce((a, r) => a + (Number(r.n) || 0), 0);

function Tile({ icon, title, big, sub, chips, color, rkey, onOpen }) {
  const clickable = !!(rkey && onOpen);
  return (
    <motion.div
      className={`hub-report${clickable ? ' is-click' : ''}`}
      variants={card}
      onClick={clickable ? () => onOpen(rkey) : undefined}
      whileHover={clickable ? { y: -2 } : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(rkey); } } : undefined}
    >
      <div className="hub-report-head" style={{ color: color || C.muted }}>
        {icon}{title}
        {clickable && <ChevronRight size={14} className="hub-report-go" />}
      </div>
      {big !== undefined && <div className="hub-report-big" style={{ color: color || C.text }}>{big}</div>}
      {sub && <div className="hub-report-sub">{sub}</div>}
      {chips && chips.length > 0 && (
        <div className="hub-report-chips">
          {chips.map((c) => <span className="hub-rpill" key={c}>{c}</span>)}
        </div>
      )}
    </motion.div>
  );
}

export default function ReportsDeck({ reports, onOpen }) {
  const fsr = reports?.fsr || { total: 0, byRegion: {} };
  const petty = reports?.finance?.pettyCash || [];
  const purchases = reports?.finance?.purchases || [];
  const attendance = reports?.attendance || {};
  const trackers = reports?.trackers || { samples: 0, jobs: 0 };
  const ehs = reports?.ehs || {};
  const compliance = reports?.compliance || {};

  const pettyPending = findStatus(petty, ['pending']);
  const pettyAmt = sumBy(petty, 'amt');
  const purchaseAmt = sumBy(purchases, 'amt');

  const attPresent = attendance.present || 0;
  const attNotSigned = attendance.notSignedIn || 0;

  return (
    <div className="hub-card">
      <div className="hub-card-title"><ClipboardCheck size={14} /> Reports &amp; Data</div>
      <motion.div className="hub-reports" initial="hidden" animate="show" variants={{ show: { transition: { staggerChildren: 0.06 } } }}>
        <Tile
          icon={<ClipboardCheck size={14} />} title="FSR Reports" color={C.cyan}
          big={fsr.total} sub="Field service reports in range"
          chips={Object.entries(fsr.byRegion).map(([k, v]) => `${regionMeta(k).short} ${v}`)}
          rkey="fsr" onOpen={onOpen}
        />
        <Tile
          icon={<Wallet size={14} />} title="Finance" color={C.green}
          big={fmtMoney(pettyAmt + purchaseAmt)} sub="Petty cash + purchases (range)"
          chips={[`Petty pending ${pettyPending}`, `Purchases ${fmtMoney(purchaseAmt)}`]}
          rkey="finance" onOpen={onOpen}
        />
        <Tile
          icon={<UserCheck size={14} />} title="Attendance" color={C.amber}
          big={attPresent} sub={`${attNotSigned} not signed in (range)`}
          rkey="attendance" onOpen={onOpen}
        />
        <Tile
          icon={<Radio size={14} />} title="Trackers" color={C.violet}
          big={trackers.jobs || 0} sub={`${(trackers.samples || 0).toLocaleString()} telemetry samples`}
          rkey="trackers" onOpen={onOpen}
        />
        <Tile
          icon={<ShieldCheck size={14} />} title="Compliance" color="#22D3EE"
          big={`${compliance.done || 0}/${compliance.roster || 0}`} sub="Acknowledged (range)"
          rkey="compliance" onOpen={onOpen}
        />
        <Tile
          icon={<CheckSquare size={14} />} title="EHS" color={C.green}
          big={`${ehs.done || 0}/${ehs.roster || 0}`} sub="Checklists done (range)"
          rkey="ehs" onOpen={onOpen}
        />
      </motion.div>
    </div>
  );
}
